<template>
    <section
        class="membership-grid"
        :class="selectedMembership ? `theme-${selectedMembership.theme}` : ''"
        ref="rootElement"
    >
        <div class="membership-grid__inner">
            <template v-if="isLoading">
                <div class="membership-grid__loading">
                    <Loading />
                </div>
            </template>

            <template v-else-if="!isLoading && selectedMembership">
                <div class="membership-grid__top-bar">
                    <h2 class="membership-grid__heading">
                        {{ props.title }}
                    </h2>

                    <ul class="membership-grid__tab-list">
                        <template v-for="membership in memberships" :key="membership.id">
                            <li class="membership-grid__tab-item">
                                <input
                                    type="radio"
                                    class="membership-grid__tab-radio"
                                    name="membership-group"
                                    :checked="selectedMembership.id === membership.id"
                                    @change="setSelectedMembership(membership)"
                                    :id="'membership-group-' + membership.id"
                                >

                                <label class="membership-grid__tab-label" :for="'membership-group-' + membership.id">
                                    <strong>{{  membership.tab_title || memberships.title }}</strong>
                                    <span v-if="membership.price_range">{{ membership.price_range }}</span>
                                </label>
                            </li>
                        </template>

                        <li class="membership-grid__tab-indicator"></li>
                    </ul>
                </div>

                <div class="membership-grid__header">
                    <div class="membership-grid__info">
                        <h3 class="membership-grid__title">
                            {{ selectedMembership.title }}
                        </h3>

                        <p class="membership-grid__suffix" v-if="selectedMembership.suffix">
                            {{ selectedMembership.suffix }}
                        </p>

                        <div class="membership-grid__description" v-if="selectedMembership.description_html" v-html="selectedMembership.description_html"></div>
                    </div>

                    <figure class="membership-grid__media o-figure">
                        <picture class="membership-grid__intrinsic-ratio">
                            <source
                                :srcset="`
                                    ${$imageUrl(selectedMembership, { width: 500, height: 359 })},
                                    ${$imageUrl(selectedMembership, { width: 1000, height: 718 })} 2x
                                `"
                                media="(max-width: 500px)"
                            >
                            <source
                                :srcset="`
                                    ${$imageUrl(selectedMembership, { width: 1000, height: 717 })},
                                    ${$imageUrl(selectedMembership, { width: 2000, height: 1434 })} 2x
                                `"
                                media="(max-width: 999px)"
                            >
                            <source
                                :srcset="`
                                    ${$imageUrl(selectedMembership, { width: 1200, height: 860 })},
                                    ${$imageUrl(selectedMembership, { width: 2400, height: 1721 })} 2x
                                `"
                                media="(min-width: 1000px)"
                            >
                            <img
                                class="membership-grid__image"
                                :src="$imageUrl(selectedMembership, { width: 500, height: 359 })"
                                width="500"
                                height="359"
                                :alt="selectedMembership.image.alt"
                            >
                        </picture>
                    </figure>
                </div>

                <div class="membership-grid__body">
                    <div class="membership-grid__table" role="table">
                        <div class="membership-grid__row membership-grid__row--header" role="row" :ref="setStickyRef">
                            <div class="membership-grid__left-column" role="columnheader">
                                <p class="membership-grid__level-tag">Select a Level:</p>
                            </div>

                            <div class="membership-grid__right-column" role="presentation">
                                <div class="membership-grid__scroll" role="presentation" :ref="setScrollElement" @scroll.passive="handleLevelScroll">
                                    <div class="membership-grid__level-list" role="presentation">
                                        <template v-for="level in levels" :key="selectedMembership.id + '-' + level.id">
                                            <div class="membership-grid__level-item" role="columnheader" :id="'membership-level-header-' + level.id" :class="isLevelSelected(level) && 'membership-grid__level-item--selected'">
                                                <button
                                                    type="button"
                                                    class="membership-grid__select-level"
                                                    @click="setSelectedLevel(level)"
                                                >
                                                    <div class="membership-grid__spacer" :ref="setWidthElement">
                                                        <strong>{{ level.title }}</strong>
                                                        <span v-if="selectedMembership.show_level_prices">from {{ $formatPrice(level.price) }}</span>
                                                    </div>
                                                </button>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <template v-for="benefit in benefits" :key="benefit.id">
                            <div class="membership-grid__row membership-grid__row--benefit" role="row">
                                <div class="membership-grid__left-column" role="rowheader" :id="'membership-benefit-row-' + benefit.id">
                                    <div class="membership-grid__benefit" v-html="benefit.description"></div>
                                </div>

                                <div class="membership-grid__right-column" role="presentation">
                                    <div class="membership-grid__scroll" role="presentation" :ref="setScrollElement" @scroll.passive="handleLevelScroll">
                                        <div class="membership-grid__level-list" role="presentation">
                                            <template v-for="(value, valueIndex) in benefit.values" :key="valueIndex">
                                                <div class="membership-grid__level-item" role="cell" :aria-labelledby="'membership-level-header-' + value.level.id + ' membership-benefit-row-' + benefit.id" :class="isLevelSelected(value.level) && 'membership-grid__level-item--selected'">
                                                    <!-- Override -->
                                                    <template v-if="value.hasValueOverride">
                                                        {{ value.valueOverride }}
                                                    </template>

                                                    <!-- No value override, checkbox -->
                                                    <template v-else-if="value.hasValue">
                                                        <svg class="o-icon o-icon--tick" role="img" :aria-label="`Included in ${value.level.title}`">
                                                            <use xlink:href="#tick"></use>
                                                        </svg>
                                                    </template>

                                                    <template v-else>
                                                        &ndash;
                                                    </template>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>

                    <form class="membership-grid__form" v-if="showForm" method="get" :action="buyUrl">
                        <div class="membership-grid__form-inner">
                            <label for="membership-grid__text" class="membership-grid__prompt" v-html="selectedLevel ? joinPrompt : 'Make a gift to the New York City Center:'" />

                            <div class="membership-grid__field">
                                &dollar;
                                <input id="membership-grid__text" type="number" class="membership-grid__text" :value="price" @input="setPrice" step="1" pattern="\d+" required />
                                <button type="submit" class="membership-grid__submit">
                                    Give Now
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </template>
        </div>
    </section>
</template>

<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { debounce } from 'lodash';

import useSticky from '../utils/use-sticky.mjs';
import Loading from '../Loading/Loading.vue';

const props = defineProps({
    title: {
        type: String,
        default: null,
    },
    fundId: {
        type: Number,
        default: null,
    },
    baseUrl: {
        type: String,
    },
    memberships: {
        type: Array,
    },
});

const isLoading = ref(true);
const memberships = ref([]);
const selectedMembership = ref(null);
const price = ref(0);
const showForm = ref(false);
const rootElement = ref(null);
const scrollElements = ref([]);
const widthElements = ref([]);

const { setStickyRef } = useSticky();

const setSelectedMembership = membership => {
    selectedMembership.value = membership;
    showForm.value = false;
    price.value = 0;
    window.location.hash = `#${membership.tab_title}`;
}

const setSelectedLevel = level => {
    price.value = level.price;
    showForm.value = true;
}

const setPrice = event => {
    price.value = parseInt(event.target.value, 10);
}

const levels = computed(() => {
    return selectedMembership.value.levels;
});

const benefits = computed(() => {
    return selectedMembership.value.benefits.map(benefit => {
        const values = selectedMembership.value.levels.map((level, levelIndex) => {
            const direct = level.benefits.find(item => {
                return item.id === benefit.id
            });

            if (direct) {
                return {
                    level,
                    hasValue: true,
                    hasValueOverride: !!direct.value,
                    valueOverride: direct.value ?? null,
                }
            }

            for (let i = levelIndex - 1; i >= 0; i--) {
                const prior = selectedMembership.value.levels[i].benefits.find(item => {
                    return item.id === benefit.id;
                });

                if (prior) {
                    return {
                        level,
                        hasValue: true,
                        hasValueOverride: !!prior.value,
                        valueOverride: prior.value ?? null,
                    }
                }
            }

            return {
                level,
                hasValue: false,
                hasValueOverride: false,
                valueOverride: null,
            };
        });

        return {
            id: benefit.id,
            description: benefit.description,
            values,
        }
    });
});

const joinPrompt = computed(() => {
    return selectedMembership.value.join_prompt_html
        .replace('{membership}', selectedMembership.value.title)
        .replace('{level}', selectedLevel.value.title);
});

const selectedLevel = computed(() => {
    return selectedMembership.value.levels
        .slice()
        .sort((a, b) => {
            return Number(b.price) - Number(a.price);
        }).find(x => {
            return Number(x.price) <= Number(price.value);
        });
});

const isLevelSelected = level => {
    return selectedLevel.value?.id === level.id;
}

const setScrollElement = element => {
    if (element) {
        // add if not already tracked
        if (!scrollElements.value.includes(element)) {
            scrollElements.value.push(element);
        }
    } else {
        // remove any refs that are no longer in the DOM
        scrollElements.value = scrollElements.value.filter(node => {
            return document.body.contains(node);
        })
    }
}

const resizeObserver = new ResizeObserver(() => {
    const maxWidth = widthElements.value.reduce((carry, item) => {
        const { width } = item.getBoundingClientRect();

        return width > carry ? width : carry;
    }, 0);

    if (!maxWidth) {
        return;
    }

    rootElement.value.style.setProperty('--auto-col-size-desktop', `${maxWidth}px`);
});

const setWidthElement = element => {
    if (element) {
        // add if not already tracked
        if (!widthElements.value.includes(element)) {
            widthElements.value.push(element);
            resizeObserver.observe(element);
        }
    } else {
        // remove any refs that are no longer in the DOM
        widthElements.value = widthElements.value.filter(node => {
            if (!document.body.contains(node)) {
                resizeObserver.unobserve(node);
                return false;
            }

            return true;
        })
    }
}

const handleLevelScroll = debounce(event => {
    for (const element of scrollElements.value) {
        if (element === event.target) {
            continue;
        }

        element.scrollLeft = event.target.scrollLeft;
    }
}, 1);

const buyUrl = computed(() => {
    return `${props.baseUrl}donate/${selectedMembership.value.fund_id ?? props.fundId}/add-to-basket/${price.value}`;
});

function hashString(str) {
    let hash = 0;

    if (str) {
        for (const char of str) {
            hash = Math.imul(31, hash) + char.charCodeAt(0) | 0;
        }
    }

    // Convert to unsigned 32-bit integer and return as hex string
    return (hash >>> 0).toString(16);
}

onBeforeMount(() => {
    if (!props.memberships.length) {
        return (isLoading.value = false);
    }

    memberships.value = props.memberships.map(membership => {
        const benefitMap = new Map();

        for (const level of membership.levels) {
            for (const benefit of level.benefits) {
                const id = hashString(benefit.description);
                benefit.id = id;

                if (!benefitMap.has(id)) {
                    benefitMap.set(id, {
                        id,
                        description: benefit.description,
                    });
                }
            }
        }

        let joinPrompt = membership.join_prompt_html;

        if (!joinPrompt?.trim().length) {
            joinPrompt = `<p>Join <em>{membership}</em> at the <em>{level}</em> level:</p>`;
        }

        membership.join_prompt_html = joinPrompt.split('</p>')[0].replace(/<p[^>]*>/, '');

        const orderedBenefits = Array.from(benefitMap.values()).sort((a, b) => {
            const aIsTaxDeductible = a.description === 'Non-deductible goods and services';
            const bIsTaxDeductible = b.description === 'Non-deductible goods and services';

            if (aIsTaxDeductible === bIsTaxDeductible) {
                return 0;
            }

            return aIsTaxDeductible ? 1 : -1;
        });

        return {
            ...membership,
            benefits: orderedBenefits,
        }
    });

    const membership = memberships.value.find(m => m.tab_title === decodeURIComponent(window.location.hash.slice(1)));
    selectedMembership.value = membership ?? memberships.value[0];
    price.value = 0;

    isLoading.value = false;
});

onBeforeUnmount(() => {
    resizeObserver.disconnect();
});
</script>
